import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { Container, Grid, Typography, Box } from "@mui/material";
import { useSnackbar } from "notistack";
import { months, sampleYear } from "../../../constants";
import { STATUS } from "../../../redux/constants";
import { addMobileCombustionValidation } from "./schema";
import {
  getEmissionFuelList,
  resetAddCombustionStatus,
  updatePurchasedGoodsAndServices,
} from "../../../redux/actions";
import CeroAutoComplete from "../../../components/CeroAutoComplete";
import CeroButton from "../../../components/CeroButton";
import CeroSelect from "../../../components/CeroSelect";
import CeroInput from "../../../components/CeroInput";
import useStyles from "./styles";
import EmissionUploadDrawer from "../../EmissionList/EmissionUploadDrawer";
import { useNavigate } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CeroItemPair from "../../../components/CeroItemPair";
import FileUpload from "../../AddEmissions/FileUpload";

const EditPurchasedGoodsAndServices = (props) => {
  const {
    onCancel,
    onUpload,
    onValidate,
    uploadDisabled,
    emissionData,
    emissionId,
  } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const isCalculateDone = useSelector(
    (state) => state.emission.updatepurchasedGoodsAndServices.isCalculateDone
  );
  const facilitiesData = useSelector(
    (state) => state.listings.listFacilities.data
  );
  const editEmissionData = useSelector(
    (state) => state.emission.updatepurchasedGoodsAndServices
  );
  const emissionPerformData = useSelector((state) => state.emission.fuelList);
  const currentYear = new Date().getFullYear().toString();
  const [selectedFile, setSelectedFile] = useState(null);

  const formik = useFormik({
    initialValues: {
      facility: emissionData.facility_id || "",
      year: emissionData.year || currentYear,
      month: emissionData.month || "",
      calculationMethod: emissionData.calculation_method || "",
      fuelUnit: emissionData.fuel_unit
        ? emissionPerformData?.data?.fuel_unit?.find(
            (item) => item.name === emissionData.fuel_unit
          )?.id || ""
        : "",
      currency: emissionData.currency
        ? emissionPerformData?.data?.currency?.find(
            (item) => item.name === emissionData.currency
          )?.id || ""
        : "",
      emissionFactor: emissionData.supplier_specific_product_ef || 0,
      emissionFactorValue: emissionData.ef_of_purchased_goods_per_mass || 0,
      quantityOfGoodsPurchased: emissionData.quantity_of_purchased_goods || 0,
      massOfPurchasedGoods: emissionData.mass_of_purchased_goods || 0,
      valueOfPurchase: emissionData.value_of_purchased_goods || 0,
      massUnit: emissionData.mass_unit
        ? emissionPerformData?.data?.mass_unit?.find(
            (item) => item.name === emissionData.mass_unit
          )?.id || ""
        : "",
      vendorCompanyName: emissionData.vendor_company_name || "",
      sector: emissionData.sector || "",
      industryName: emissionData.industry || "",
      industrySubCategory: emissionData.industry_sub_category || "",
    },
    validationSchema: addMobileCombustionValidation,
    onSubmit: () => {},
    enableReinitialize: true,
  });
  const facilitiesList = facilitiesData.map((item) => ({
    key: item.id,
    value: item.name,
  }));
  const yearList = sampleYear().map((item) => ({
    id: item.key,
    label: item.value,
  }));
  const unitMap = {};

  const calculationNamesMap = {};
  const calculationInputList =
    emissionPerformData?.data?.calculation_method?.map((item) => {
      calculationNamesMap[item.id] = item.name; // Populate namesMap
      return {
        key: item.id,
        value: item.name,
      };
    }) || [];

  const currencyMap = {};
  const currencyList = emissionPerformData?.data?.currency?.map((item) => {
    currencyMap[item.id] = item.name;
    return {
      key: item.id,
      value: item.name,
    };
  });
  const sectorList = emissionPerformData?.data?.sectors?.map((item) => ({
    key: item.id,
    value: item.name,
  }));
  const industryList = () => {
    return formik.values.sector
      ? emissionPerformData?.data?.industries
          ?.filter((item) => item.sector_id === formik.values.sector)
          ?.map((item) => ({
            key: item.id,
            value: item.name,
          })) || []
      : [];
  };

  const subCategoryList = () => {
    return formik.values.industryName
      ? emissionPerformData?.data?.industry_sub_sector
          ?.filter((item) => item.industry_id === formik.values.industryName)
          ?.map((item) => ({
            key: item.id,
            value: item.name,
          })) || []
      : [];
  };
  const massUnitList = emissionPerformData?.data?.mass_unit?.map((item) => {
    unitMap[item.id] = item.name;
    return {
      key: item.id,
      value: item.name,
    };
  });
  useEffect(() => {
    dispatch(getEmissionFuelList("purchased_goods_and_services"));
    return () => {
      dispatch(resetAddCombustionStatus());
    };
  }, [dispatch]);

  useEffect(() => {
    if (editEmissionData.status === STATUS.SUCCESS) {
      enqueueSnackbar(
        "Purchased goods and services record updated successfully",
        {
          variant: "success",
        }
      );
      dispatch(resetAddCombustionStatus());
      onCancel();
    } else if (editEmissionData.status === STATUS.ERROR) {
      enqueueSnackbar(
        editEmissionData.message.message ||
          "We couldn't process your request. Please try again later.",
        { variant: "error" }
      );
    }
  }, [editEmissionData, dispatch, enqueueSnackbar, onCancel]);

  const onCalculate = () => {
    const requestData = {
      id: emissionId,
      facility_id: formik.values.facility,
      emission_type: formik.values.emissionType,
      calculation_method: formik.values.calculationMethod,
      year: formik.values.year,
      month: formik.values.month,
      unit: formik.values.fuelUnit,
      vendor_company_name: formik.values.vendorCompanyName,
      sector: formik.values.sector,
      industry: formik.values.industryName,
      industry_sub_category: formik.values.industrySubCategory,
      currency: currencyMap[formik.values.currency],
      mass_unit: unitMap[formik.values.massUnit],
      value_of_purchased_goods: formik.values.valueOfPurchase,
      mass_of_purchased_goods: formik.values.massOfPurchasedGoods,
      supplier_specific_product_ef: formik.values.emissionFactor,
      quantity_of_purchased_goods: formik.values.quantityOfGoodsPurchased,
      ef_of_purchased_goods_per_mass: formik.values.emissionFactorValue,
      save: false,
    };
    dispatch(updatePurchasedGoodsAndServices(requestData));
  };

  const onUpdatePurchasedGoodsData = () => {
    const requestData = {
      id: emissionId,
      facility_id: formik.values.facility,
      year: formik.values.year,
      month: formik.values.month,
      calculation_method: formik.values.calculationMethod,
      unit: formik.values.fuelUnit,
      vendor_company_name: formik.values.vendorCompanyName,
      sector: formik.values.sector,
      industry: formik.values.industryName,
      industry_sub_category: formik.values.industrySubCategory,
      currency: currencyMap[formik.values.currency],
      mass_unit: unitMap[formik.values.massUnit],
      value_of_purchased_goods: formik.values.valueOfPurchase,
      mass_of_purchased_goods: formik.values.massOfPurchasedGoods,
      supplier_specific_product_ef: formik.values.emissionFactor,
      quantity_of_purchased_goods: formik.values.quantityOfGoodsPurchased,
      ef_of_purchased_goods_per_mass: formik.values.emissionFactorValue,
      save: true,
      file: selectedFile,
    };
    sessionStorage.setItem("year", requestData.year);
    dispatch(updatePurchasedGoodsAndServices(requestData));
  };
  const [isOpenEmissionTypeUpload, setIsOpenEmissionTypeUpload] =
    useState(false);
  const onCloseDrawerUpload = () => {
    setIsOpenEmissionTypeUpload(false);
  };
  const handleFileUpload = (file) => {
    setSelectedFile(file);
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
  };

  return (
    <>
      <div className={classes.backContainer}>
        <div
          className={classes.backButtonDiv}
          onClick={() => {
            navigate(-1);
          }}
        >
          <ArrowBackIosIcon />
          Back
        </div>
      </div>
      <Container className={classes.container}>
        <Box className={classes.innerContainer}>
          <Typography className={classes.title} variant="h6" component="div">
            Edit Purchased Goods And Services
          </Typography>
          <Box className={classes.topContainer}>
            <Grid className={classes.dynamicBoard}>
              <Grid item className={classes.individualInput}>
                <CeroSelect
                  required
                  id="facility"
                  name="facility"
                  label="Facility"
                  fullWidth
                  options={facilitiesList}
                  selectedValue={formik.values.facility}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.facility && formik.errors.facility}
                />
              </Grid>
              <Grid item className={classes.individualInput}>
                <CeroAutoComplete
                  id="year"
                  name="year"
                  label="Assessment Year"
                  onChange={(e, value) =>
                    formik.setFieldValue("year", value.id)
                  }
                  onBlur={formik.handleBlur}
                  error={formik.touched.year && formik.errors.year}
                  options={yearList}
                  value={formik.values.year}
                />
              </Grid>
              <Grid item className={classes.individualInput}>
                <CeroSelect
                  required
                  id="month"
                  name="month"
                  label="Month"
                  fullWidth
                  options={months}
                  selectedValue={formik.values.month}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.month && formik.errors.month}
                />
              </Grid>
              <Grid item className={classes.individualInput}>
                <CeroSelect
                  required
                  id="calculationMethod"
                  name="calculationMethod"
                  label="Calculation Method"
                  fullWidth
                  options={calculationInputList}
                  selectedValue={formik.values.calculationMethod}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.calculationMethod &&
                    formik.errors.calculationMethod
                  }
                />
              </Grid>

              {formik.values.calculationMethod &&
                calculationNamesMap[
                  formik.values.calculationMethod
                ]?.toLowerCase() === "supplier-specific method" && (
                  <>
                    <Grid item className={classes.individualInput}>
                      <CeroInput
                        required
                        id="quantityOfGoodsPurchased"
                        name="quantityOfGoodsPurchased"
                        label="Quantity of goods purchased in kg"
                        value={formik.values.quantityOfGoodsPurchased}
                        fullWidth
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.quantityOfGoodsPurchased &&
                          formik.errors.quantityOfGoodsPurchased
                        }
                        type="number"
                      />
                    </Grid>
                    <Grid item className={classes.individualInput}>
                      <CeroInput
                        required
                        id="emissionFactor"
                        name="emissionFactor"
                        label="Supplier specific emission factor of purchased goods and service"
                        value={formik.values.emissionFactor}
                        fullWidth
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.emissionFactor &&
                          formik.errors.emissionFactor
                        }
                        type="number"
                      />
                    </Grid>
                  </>
                )}
              {formik.values.calculationMethod &&
                calculationNamesMap[
                  formik.values.calculationMethod
                ]?.toLowerCase() === "average-data method" && (
                  <>
                    <Grid item className={classes.individualInput}>
                      <CeroInput
                        required
                        id="massOfPurchasedGoods"
                        name="massOfPurchasedGoods"
                        label="Mass of purchased goods or service"
                        value={formik.values.massOfPurchasedGoods}
                        fullWidth
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.massOfPurchasedGoods &&
                          formik.errors.massOfPurchasedGoods
                        }
                        type="number"
                      />
                    </Grid>
                    <Grid item className={classes.individualInput}>
                      <CeroSelect
                        required
                        id="massUnit"
                        name="massUnit"
                        label="Unit"
                        options={massUnitList}
                        fullWidth
                        selectedValue={formik.values.massUnit}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.massUnit && formik.errors.massUnit
                        }
                        classes={{
                          input: classes.input,
                          container: classes.selectContainer,
                        }}
                      />
                    </Grid>
                    <Grid item className={classes.individualInput}>
                      <CeroInput
                        required
                        id="emissionFactorValue"
                        name="emissionFactorValue"
                        label="Emission factor for goods or service per unit of mass"
                        value={formik.values.emissionFactorValue}
                        fullWidth
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.emissionFactorValue &&
                          formik.errors.emissionFactorValue
                        }
                        type="number"
                      />
                    </Grid>
                  </>
                )}
              {formik.values.calculationMethod &&
                calculationNamesMap[
                  formik.values.calculationMethod
                ]?.toLowerCase() === "spend-based" && (
                  <>
                    <Grid item className={classes.individualInput}>
                      <CeroInput
                        required
                        id="vendorCompanyName"
                        name="vendorCompanyName"
                        label="Vendor Company Name"
                        value={formik.values.vendorCompanyName}
                        fullWidth
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.vendorCompanyName &&
                          formik.errors.vendorCompanyName
                        }
                      />
                    </Grid>
                    <Grid item className={classes.individualInput}>
                      <CeroSelect
                        required
                        id="sector"
                        name="sector"
                        label="Sector"
                        fullWidth
                        options={sectorList}
                        selectedValue={formik.values.sector}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.sector && formik.errors.sector}
                        classes={{
                          input: classes.input,
                          container: classes.selectContainer,
                        }}
                      />
                    </Grid>
                    <Grid item className={classes.individualInput}>
                      <CeroSelect
                        required
                        id="industryName"
                        name="industryName"
                        label="Indusrty"
                        options={industryList()}
                        selectedValue={formik.values.industryName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.industryName &&
                          formik.errors.industryName
                        }
                        classes={{
                          input: classes.input,
                          container: classes.selectContainer,
                        }}
                      />
                    </Grid>
                    <CeroSelect
                      required
                      id="industrySubCategory"
                      name="industrySubCategory"
                      label="Industry Subcategory"
                      options={subCategoryList()}
                      selectedValue={formik.values.industrySubCategory}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.industrySubCategory &&
                        formik.errors.industrySubCategory
                      }
                      classes={{
                        input: classes.input,
                        container: classes.selectContainer,
                      }}
                    />
                    <Grid item className={classes.individualInput}>
                      <CeroInput
                        required
                        id="valueOfPurchase"
                        name="valueOfPurchase"
                        label="Value of purchased good or service"
                        value={formik.values.valueOfPurchase}
                        fullWidth
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.valueOfPurchase &&
                          formik.errors.valueOfPurchase
                        }
                        type="number"
                      />
                    </Grid>
                    <Grid item className={classes.individualInput}>
                      <CeroSelect
                        required
                        id="currency"
                        name="currency"
                        label="Currency"
                        fullWidth
                        options={currencyList}
                        selectedValue={formik.values.currency || ""}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.currency && formik.errors.currency
                        }
                      />
                    </Grid>
                  </>
                )}
            </Grid>
            <CeroButton
              buttonText="Calculate"
              className={classes.buttonPrimary}
              onClick={onCalculate}
              disabled={!formik.dirty || !formik.isValid}
            />
            <CeroButton
              buttonText="Clear"
              variant="outlined"
              className={classes.buttonSecondary}
              onClick={formik.resetForm}
            />

            {isCalculateDone &&
              (() => {
                const leftItems = [
                  {
                    key: "co2",
                    title: (
                      <>
                        CO<sub>2</sub>
                      </>
                    ),
                    unit: "tonnes",
                  },
                  {
                    key: "ch4",
                    title: (
                      <>
                        CH<sub>4</sub>
                      </>
                    ),
                    unit: "tonnes",
                  },
                  {
                    key: "co2e",
                    title: (
                      <>
                        CO<sub>2</sub>e
                      </>
                    ),
                    unit: "tonnes",
                  },
                ].filter(({ key }) => editEmissionData.data[key] != null);

                const rightItems = [
                  {
                    key: "n2o",
                    title: (
                      <>
                        N<sub>2</sub>O
                      </>
                    ),
                    unit: "tonnes",
                  },
                  {
                    key: "ef",
                    title: <>EF</>,
                    customValue: (val) => (
                      <>
                        {val} kgCO<sub>2</sub>e/Unit
                      </>
                    ),
                  },
                ].filter(({ key }) => editEmissionData.data[key] != null);

                const showDivider =
                  leftItems.length > 0 && rightItems.length > 0;

                return (
                  <Box className={classes.bottomContainer}>
                    <Typography
                      variant="h6"
                      component="h6"
                      className={classes.previewTitle}
                    >
                      Emission Preview
                    </Typography>

                    <Grid
                      container
                      direction="row"
                      wrap="nowrap"
                      spacing={4}
                      justifyContent="space-between"
                    >
                      {/* Left Column */}
                      <Grid
                        item
                        container
                        direction="column"
                        xs={12}
                        md={6}
                        sx={
                          showDivider
                            ? { borderRight: "1px solid #ccc", pr: 2 }
                            : { pr: 2 }
                        }
                      >
                        {leftItems.map(({ key, title, unit }) => (
                          <CeroItemPair
                            key={key}
                            title={title}
                            value={`${editEmissionData.data[key]} ${unit}`}
                            classes={{
                              container: classes.infoContainer,
                              title: classes.emissionTitle,
                              value: classes.value,
                            }}
                          />
                        ))}
                      </Grid>

                      {/* Right Column */}
                      {rightItems.length > 0 && (
                        <Grid
                          item
                          container
                          direction="column"
                          xs={12}
                          md={6}
                          sx={{ pl: 2 }}
                        >
                          {rightItems.map(
                            ({ key, title, unit, customValue }) => (
                              <CeroItemPair
                                key={key}
                                title={title}
                                value={
                                  customValue
                                    ? customValue(editEmissionData.data[key])
                                    : `${editEmissionData.data[key]} ${unit}`
                                }
                                classes={{
                                  container: classes.infoContainer,
                                  title: classes.emissionTitle,
                                  value: classes.value,
                                }}
                              />
                            )
                          )}
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                );
              })()}
          </Box>
          <FileUpload
            onFileUpload={handleFileUpload}
            onFileRemove={handleFileRemove}
          />
        </Box>
        <Box className={classes.buttonContainer}>
          <CeroButton
            buttonText="Update"
            disabled={
              !isCalculateDone || editEmissionData.status === STATUS.RUNNING
            }
            className={classes.buttonPrimary}
            onClick={() => onUpdatePurchasedGoodsData(formik.values)}
          />
        </Box>
        <EmissionUploadDrawer
          isOpen={isOpenEmissionTypeUpload}
          onClose={onCloseDrawerUpload}
          onUpload={onUpload}
          onValidate={onValidate}
          uploadDisabled={uploadDisabled}
        />
      </Container>
    </>
  );
};

export default EditPurchasedGoodsAndServices;
